import { Component, Element, Event, EventEmitter, Listen, Method, Prop, h } from '@stencil/core';
import { focusElement, generateId } from '../../utils/functions';

/**
 * Toggles give users the ability to choose between two states.
 */
/**
 * @slot label - Label for the toggle. Alternatively, you can use the label attribute.
 */
@Component({
  tag: 'som-toggle',
  styleUrl: 'som-toggle.scss',
})
export class SomToggle {
  @Element() el: HTMLElement;

  /**
   * This property can be used to set the inline label.
   */
  @Prop() label: string = '';

   /**
   * This property will visually hide the label.  A label is still required to be provided for screenreader support.
   */
   @Prop() hideLabel: boolean = false;

   /**
   * This property is a list of CSS classes that can be used to style the label.  
   * @deprecated with version 4.0.1 release
   */
  @Prop() labelClass?: string = '';
  
  /**
   * This property determines the size of the toggle
   */
  @Prop() size: 'sm' | 'lg' = 'sm';

  /**
   * This property can be used to set the ARIA label associated with this input (for screen readers).
   */
  @Prop() inputAriaLabel: string | null = null;

  /**
   * This property is the form element name (used when submitting the form).
   */
  @Prop() name: string = '';

  /**
   * This boolean is used to set the toggle as "checked".
   */
  @Prop({ mutable: true, reflect: true })
  checked: boolean = false;

  /**
   * This property is the toggle value sent on submit when using it in a form.
   */
  @Prop() value: string = '';

  /**
   * This boolean is used to disable the toggle.
   */
  @Prop() disabled: boolean = false;

  /**
   * This property can be used to specifically set an ID to the input field. If an ID is not provided, it will be dynamically generated.
   */
  @Prop() inputId: string = generateId('som-input');

  /**
   * This event is emitted when when the toggle gets focus.
   */
  @Event() somFocus: EventEmitter;

  /**
   * This event is emitted when when the toggle loses focus.
   */
  @Event() somBlur: EventEmitter;

  /**
   * This event is emitted when the toggle value changes.
   */
  @Event() somChange: EventEmitter;

  inputElement: HTMLInputElement;

  toggleElement: HTMLDivElement;

  toggleId: string;

  helpElementId: string;

  handleFocusCb;

  handleBlurCb;

  handleChangeCb;

  @Listen('keydown')
  handleKeyDown(e) {
    if (e.keyCode === 32) {
      this.handleChange(e);
    }
  }

  /**
   * This public method sets focus to the checkbox element.
   * @returns Promise will resolve after focus.
   */
  @Method()
  setFocus(): Promise<void> {
    return focusElement(this.toggleElement);
  }

  handleFocus() {
    this.somFocus.emit();
  }

  handleBlur() {
    this.somBlur.emit();
  }

  handleChange(e) {
    e.preventDefault();
    e.stopPropagation();

    this.checked = !this.checked;
    this.somChange.emit();
  }

  registerEvents() {
    this.inputElement.addEventListener('change', this.handleChangeCb);
  }

  unregisterEvents() {
    this.inputElement.removeEventListener('change', this.handleChangeCb);
  }

  componentWillLoad() {
    this.helpElementId = `${this.inputId}-help`;
    this.toggleId = `${this.inputId}-toggle`;

    this.handleFocusCb = this.handleFocus.bind(this);
    this.handleBlurCb = this.handleBlur.bind(this);
    this.handleChangeCb = this.handleChange.bind(this);
  }

  componentDidLoad() {
    this.inputElement = this.el.querySelector(`#${this.inputId}`);
    this.toggleElement = this.el.querySelector(`#${this.toggleId}`);
    this.registerEvents();
  }

  componentWillUnload() {
    this.unregisterEvents();
  }

  render() {
    const ariaChecked = this.checked ? { 'aria-checked': 'true' } : { 'aria-checked': 'false' };
    const ariaDisabled = this.disabled ? { 'aria-disabled': 'true' } : { 'aria-disabled': 'false' };

    return (
      <div class="form-group">
        <div class={`toggle-container toggle-${this.size}`}>
          <div
            id={this.toggleId}
            class={['toggle', this.checked ? 'checked' : '', this.disabled ? 'disabled' : ''].join(' ')}
            role="checkbox"
            tabindex={this.disabled ? '' : '0'}
            onClick={this.disabled ? null : this.handleChangeCb}
            onFocus={this.handleFocusCb}
            onBlur={this.handleBlurCb}
            aria-labelledby={`${this.inputId}-label`}
            aria-label={this.inputAriaLabel}
            {...ariaDisabled}
            {...ariaChecked}
          >
            <input id={this.inputId} type="checkbox" tabindex="-1" name={this.name} value={this.value} checked={this.checked} disabled={this.disabled} />
            <div class="handle">
              {this.checked ? <som-icon name="checkmark" display="block"></som-icon> : <som-icon name="close" display="block"></som-icon>}
            </div>
          </div>
          
          <label id={`${this.inputId}-label`} htmlFor={this.inputId} class={`toggle-label ${this.labelClass} ${this.hideLabel ? 'sr-only' : ''} d-block`}>
            <slot name="label">{this.label}</slot>
          </label>
        </div>
      </div>
    );
  }
}
